import React, { useState, useRef } from "react";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import api, { fileUrl, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { User, Upload as UploadIcon, Instagram, Globe, Twitter } from "lucide-react";

export default function ProfileEditor() {
  const { t } = useI18n();
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    bio: user?.bio || "",
    instagram: user?.instagram || "",
    website: user?.website || "",
    twitter: user?.twitter || "",
    tiktok: user?.tiktok || "",
    tax_id: user?.tax_id || "",
    is_business: !!user?.is_business,
    irpf_pct: user?.irpf_pct ?? "",
  });
  const [picture, setPicture] = useState(user?.picture || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPicture(data.path);
      setUser({ ...user, picture: data.path });
      toast.success("Foto actualizada");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.irpf_pct === "" || payload.irpf_pct === null) {
        delete payload.irpf_pct;
      } else {
        payload.irpf_pct = parseFloat(payload.irpf_pct);
      }
      const { data } = await api.put("/profile", payload);
      setUser(data);
      toast.success(t.profile.saved);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const inputCls = "w-full border border-neutral-300 px-4 py-3 outline-none focus:border-black bg-white text-sm";
  const socialCls = "w-full border border-neutral-300 pl-10 pr-3 py-2.5 outline-none focus:border-black bg-white text-sm";

  return (
    <div data-testid="profile-editor">
      <h1 className="font-serif text-4xl font-light tracking-tight mb-2">{t.profile.title}</h1>
      <p className="text-sm text-neutral-500 mb-8">{t.profile.subtitle}</p>

      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4">
          <div className="aspect-square bg-neutral-100 border border-neutral-200 overflow-hidden flex items-center justify-center">
            {picture ? <img src={fileUrl(picture)} alt={form.name} className="w-full h-full object-cover" data-testid="profile-picture" />
              : <User size={64} strokeWidth={1} className="text-neutral-400" />}
          </div>
          <input ref={fileRef} type="file" accept="image/*" onChange={onFileChange} className="hidden" data-testid="profile-picture-input" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} className="mt-3 w-full border border-black py-3 overline hover:bg-neutral-100 transition-colors flex items-center justify-center gap-2 disabled:opacity-50" data-testid="profile-picture-btn">
            <UploadIcon size={14} strokeWidth={1.5} /> {uploading ? "..." : t.profile.changePicture}
          </button>
          {user?.role === "artist" && (
            <a href={`/artists/${user?.user_id}`} target="_blank" rel="noreferrer" className="mt-2 block text-center overline text-neutral-500 hover:text-black py-2" data-testid="profile-view-public">
              {t.profile.viewPublic} →
            </a>
          )}
        </div>

        <form onSubmit={save} className="lg:col-span-8 space-y-4">
          <div>
            <label className="overline text-neutral-500 mb-2 block">{t.profile.name}</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="profile-name" />
          </div>
          <div>
            <label className="overline text-neutral-500 mb-2 block">{t.profile.bio}</label>
            <textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder={t.profile.bioPlaceholder}
              className={`${inputCls} h-44 resize-none`} maxLength={1500} data-testid="profile-bio" />
            <div className="text-xs text-neutral-400 mt-1 text-right">{(form.bio || "").length}/1500</div>
          </div>

          {user?.role === "artist" && (
            <div>
              <label className="overline text-neutral-500 mb-3 block">{t.profile.social}</label>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="relative">
                  <Instagram size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" strokeWidth={1.5} />
                  <input value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} placeholder="@usuario" className={socialCls} data-testid="profile-instagram" />
                </div>
                <div className="relative">
                  <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" strokeWidth={1.5} />
                  <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://..." className={socialCls} data-testid="profile-website" />
                </div>
                <div className="relative">
                  <Twitter size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" strokeWidth={1.5} />
                  <input value={form.twitter} onChange={(e) => setForm({ ...form, twitter: e.target.value })} placeholder="@usuario" className={socialCls} data-testid="profile-twitter" />
                </div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 text-xs font-bold">TT</span>
                  <input value={form.tiktok} onChange={(e) => setForm({ ...form, tiktok: e.target.value })} placeholder="@usuario" className={socialCls} data-testid="profile-tiktok" />
                </div>
              </div>
            </div>
          )}

          {(user?.role === "artist" || user?.role === "referrer") && (
            <div className="pt-2 border-t border-neutral-200">
              <label className="overline text-neutral-500 mb-3 block">Datos fiscales (España)</label>
              <p className="text-xs text-neutral-500 mb-3 leading-relaxed">
                Necesarios para liquidar tus ventas. Si eres autónomo emites factura con IVA + retención IRPF que ingresamos a tu nombre.
                Si no lo eres, te retenemos IRPF como pago a profesional y lo declaramos en el modelo 111/190.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-neutral-600 mb-1 block">NIF / CIF</label>
                  <input value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} placeholder="12345678A" className="w-full border border-neutral-300 px-3 py-2.5 text-sm" data-testid="profile-tax-id" />
                </div>
                <div>
                  <label className="text-xs text-neutral-600 mb-1 block">% IRPF (opcional)</label>
                  <input type="number" min="0" max="0.5" step="0.01" value={form.irpf_pct} onChange={(e) => setForm({ ...form, irpf_pct: e.target.value })} placeholder="0.15 = 15%" className="w-full border border-neutral-300 px-3 py-2.5 text-sm" data-testid="profile-irpf" />
                  <div className="text-[10px] text-neutral-500 mt-1">Vacío usa el valor por defecto de la galería</div>
                </div>
                <label className="sm:col-span-2 flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.is_business} onChange={(e) => setForm({ ...form, is_business: e.target.checked })} data-testid="profile-is-business" />
                  <span>Soy <strong>autónomo</strong> y emito factura con IVA + IRPF</span>
                </label>
              </div>
            </div>
          )}

          <button disabled={saving} type="submit" className="bg-black text-white px-10 py-4 overline hover:bg-neutral-800 transition-colors disabled:opacity-50" data-testid="profile-save">
            {saving ? "..." : t.profile.save}
          </button>
        </form>
      </div>
    </div>
  );
}
